import {
  type ComponentPropsWithRef,
  type ElementType,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react'
import { useIntl } from 'react-intl'
import { Link as MaterialLink } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { type Locale } from 'invictus-sdk-typescript'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { ENTER } from '@Constants/keyboard'
import { DEFAULT_LOCALE } from '@Constants/locale'
import VisuallyHiddenLabel from '@DS/components/components/utils/aria/VisuallyHiddenLabel'
import { useFullRefreshReload } from '@Hooks/useFullRefreshReload'
import { type Pathname } from '@Types/navigation/navigation'
import {
  getPathLocale,
  getPathWithoutLocale,
  getRelativeUrl,
  isHomepage,
  isPathname,
  removeBasePathPrefix,
} from '@Utils/router'

import { type LinkBaseProps } from './LinkBase.types'
import * as styles from './styles'

const getTargetPath = (
  hasFullReloadBehavior: boolean,
  locale: string | false | undefined,
  href: string | undefined,
): string | undefined => {
  if (hasFullReloadBehavior && isPathname(href)) {
    return getRelativeUrl(href, locale as Locale | undefined)
  }

  if (isHomepage(href) && locale && locale !== DEFAULT_LOCALE) {
    return `/${locale}`
  }

  return href
}

const getSkipBasePathNavigationTarget = (
  href: Pathname,
  locale: string | false | undefined,
): { locale?: Locale; path: Pathname } => {
  const pathWithoutBase = removeBasePathPrefix(href) as Pathname
  const localeFromHref = getPathLocale(pathWithoutBase)

  return {
    locale: localeFromHref ?? (locale as Locale | undefined),
    path: (localeFromHref ? getPathWithoutLocale(pathWithoutBase) : pathWithoutBase) as Pathname,
  }
}

const LinkBase = forwardRef<HTMLAnchorElement, LinkBaseProps<ElementType>>(
  (
    {
      children,
      href,
      locale,
      icon: Icon,
      title,
      variant = 'subtitle1',
      isExternal: isExternalProp = false,
      isNextRoute = false,
      hasNoSpacing = false,
      hasDefaultFocusVisible = false,
      isInline = false,
      isFullWidth = false,
      isObfuscated = false,
      hasFullReloadBehavior = false,
      skipBasePath = false,
      surface = 'onDefault',
      align = 'center',
      iconProps,
      'aria-label': ariaLabel,
      ...rest
    }: LinkBaseProps<ElementType>,
    ref: ComponentPropsWithRef<ElementType>['ref'],
  ): ReactElement | null => {
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const { push } = useRouter()
    const { goToWithFullReload } = useFullRefreshReload()
    const component = rest.component || 'a'

    const isExternal = isExternalProp || href?.includes('://')

    const isAnchor = !!href?.startsWith('#')

    const hasToUseLink = component === 'a' && !isObfuscated

    const hasToUseNextLink =
      hasToUseLink && isNextRoute && !isExternal && !isAnchor && !hasFullReloadBehavior && !!href && !isHomepage(href)

    const targetPath = getTargetPath(hasFullReloadBehavior, locale, href)

    const linkProps = {
      ...(targetPath && ({ href: targetPath } satisfies LinkBaseProps<'a'>)),
      ...(isExternal &&
        ({
          target: '_blank',
          title,
          rel: rest.rel || 'noopener noreferrer',
        } satisfies LinkBaseProps<'a'>)),
    }

    const openLink = async () => {
      if (!href) {
        return
      }

      if (isExternal) {
        window.open(targetPath, '_blank')

        return
      }

      if (hasFullReloadBehavior) {
        goToWithFullReload(href as Pathname, locale as Locale | undefined)

        return
      }

      if (skipBasePath) {
        const { path, locale: nextLocale } = getSkipBasePathNavigationTarget(href as Pathname, locale)

        goToWithFullReload(path, nextLocale, true)

        return
      }

      if (locale) {
        await push(href, undefined, { locale })

        return
      }

      await push(href)
    }

    const buttonProps = {
      component: rest.component || 'button',
      tabIndex: rest.tabIndex || 0,
      title,
      onClick: (event: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) => {
        rest.onClick?.(event)
        void openLink()
      },
      onKeyDown: (event: KeyboardEvent<HTMLButtonElement> & KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === ENTER) {
          rest.onKeyDown?.(event)
          void openLink()
        }
      },
    } satisfies LinkBaseProps<'button'>

    return (
      <MaterialLink
        {...rest}
        {...(hasToUseNextLink && { component: NextLink, ...(locale && { locale }) })}
        {...(hasToUseLink ? linkProps : buttonProps)}
        css={[
          styles.link(theme, surface, isInline, hasDefaultFocusVisible, align),
          hasNoSpacing && styles.noSpacing(theme, hasDefaultFocusVisible),
          isFullWidth && styles.fullWidth,
        ]}
        ref={ref}
        variant={variant}
        role={component === 'a' && isObfuscated ? 'link' : undefined}
        aria-label={ariaLabel}
      >
        {children}
        {isExternal && !ariaLabel && (
          <VisuallyHiddenLabel label={formatMessage({ id: 'link_newTab_titleSuffix' })} component="span" />
        )}

        {Icon && <Icon {...iconProps} css={styles.icon(isInline)} />}
      </MaterialLink>
    )
  },
)

export default LinkBase
